import { contentStand, type ContentStand } from "../../../lib/medien";

/**
 * Ordner-Ansicht der Content-Ablage (0155, public.content_library): Kooperation → Monat → Beitrag mit Fassungen, Dateien,
 * Stand bei der Marke und offenen Kommentaren. Reine Funktionen, getestet in apps/web/test/content-ablage.test.ts.
 */

export interface LibLimits {
  storage_bytes: number;
  storage_gb: number;
  video_max: number;
  video_mb: number;
  videos_used: number;
  brand_review: boolean;
  brand_review_trial: boolean;
}
export interface LibItem { id: string; label: string; status: string; platform: string; format: string; draft_due: string | null; publish_on: string | null }
export interface LibPeriod { id: string; month: string; status: string; items: LibItem[] }
export interface LibBrand { id: string; status: string; expires_at: string | null; open_count: number; decided_at: string | null; recipient: string }
export interface LibSub {
  id: string; period_id: string; deliverable_id: string | null; version: number; status: "submitted" | "changes_requested" | "approved";
  caption: string | null; feedback: string | null; submitted_at: string; reviewed_at: string | null; media_ids: string[]; attachment_ids: string[];
  brand: LibBrand | null;
}
export interface LibMedia {
  id: string; period_id: string; deliverable_id: string | null; submission_id: string | null; kind: "video" | "image" | "pdf"; mime: string; name: string;
  size: number; status: "uploading" | "ready"; sha256: string | null; duration_ms: number | null; width: number | null; height: number | null;
  poster: boolean; uploaded_side: "staff" | "talent"; created_at: string; comments_open: number;
}
export interface LibDeal {
  id: string; title: string; status: string; talent_id: string; talent: string; brand: string | null; brand_id: string; can_write: boolean;
  periods: LibPeriod[]; submissions: LibSub[]; media: LibMedia[];
}
export interface Library { limits: LibLimits; keep_old_days: number | null; deals: LibDeal[] }

export function parseLibrary(raw: unknown): Library {
  const o = (typeof raw === "string" ? JSON.parse(raw) : raw ?? {}) as Partial<Library>;
  const l = (o.limits ?? {}) as Partial<LibLimits>;
  return {
    limits: { storage_bytes: Number(l.storage_bytes ?? -1), storage_gb: Number(l.storage_gb ?? -1), video_max: Number(l.video_max ?? -1), video_mb: Number(l.video_mb ?? 2048),
      videos_used: Number(l.videos_used ?? 0), brand_review: l.brand_review === true, brand_review_trial: l.brand_review_trial === true },
    keep_old_days: typeof o.keep_old_days === "number" ? o.keep_old_days : null,
    deals: Array.isArray(o.deals) ? o.deals.map((d) => ({ ...d, periods: d.periods ?? [], submissions: d.submissions ?? [], media: d.media ?? [] })) : [],
  };
}

/** Ein „Beitrag“ im Ordner: ein Beitrag der Kooperation oder der Monat selbst (Inhalt ohne Beitrag) */
export interface Kachel {
  key: string;
  deal: LibDeal;
  period: LibPeriod;
  item: LibItem | null;
  label: string;
  fassungen: LibSub[];
  letzte: LibSub | null;
  stand: ContentStand | "leer";
  /** hochgeladen, aber noch in keiner Fassung */
  neu: LibMedia[];
  vorschau: LibMedia | null;
  kommentare: number;
}

export function kachelKey(item: LibItem | null, period: LibPeriod): string {
  return item ? item.id : `monat:${period.id}`;
}

export function kacheln(deal: LibDeal, period: LibPeriod, monatLabel: (m: string) => string, jetzt = new Date()): Kachel[] {
  const out: Kachel[] = [];
  const aus = (item: LibItem | null): Kachel => {
    const passt = <T extends { deliverable_id: string | null; period_id: string }>(x: T) => (item ? x.deliverable_id === item.id : x.deliverable_id === null && x.period_id === period.id);
    const fassungen = deal.submissions.filter(passt).sort((a, b) => b.version - a.version);
    const letzte = fassungen[0] ?? null;
    // nur fertige Uploads (liegengebliebene Reste eines abgebrochenen Uploads zeigen nichts zum Öffnen)
    const neu = deal.media.filter((m) => passt(m) && !m.submission_id && m.status === "ready");
    const dateien = letzte ? letzte.media_ids.map((id) => deal.media.find((m) => m.id === id)).filter((m): m is LibMedia => !!m) : [];
    const vorschau = dateien.find((m) => m.kind === "video") ?? dateien[0] ?? neu.find((m) => m.status === "ready") ?? null;
    return {
      key: kachelKey(item, period), deal, period, item, label: item ? item.label : monatLabel(period.month), fassungen, letzte,
      stand: letzte ? contentStand(letzte, jetzt) : "leer", neu, vorschau,
      kommentare: deal.media.filter((m) => passt(m)).reduce((s, m) => s + Number(m.comments_open ?? 0), 0),
    };
  };
  for (const item of period.items) out.push(aus(item));
  const monat = aus(null);
  if (monat.fassungen.length || monat.neu.length) out.push(monat);
  return out;
}

export type Filter = "alle" | ContentStand | "leer";

export function passtFilter(k: Kachel, filter: Filter, suche: string): boolean {
  // „Alle“ zeigt, was Content hat (Fassung oder neuer Upload); Beiträge ohne alles stehen unter „Noch ohne Content“
  if (filter === "alle" ? k.stand === "leer" && !k.neu.length : k.stand !== filter) return false;
  const q = suche.trim().toLowerCase();
  if (!q) return true;
  return [k.deal.title, k.deal.talent, k.deal.brand ?? "", k.label, k.period.month].some((x) => x.toLowerCase().includes(q));
}

/** Darf diese Fassung (noch) an die Marke? Eingereicht, oder freigegeben, die neueste, zu einem Beitrag, nicht live und nicht schon von der Marke freigegeben */
export function teilbar(k: Kachel, sub: LibSub | null): boolean {
  if (!sub || sub !== k.letzte) return false;
  if (sub.status === "submitted") return true;
  return sub.status === "approved" && !!k.item && !["live", "cancelled"].includes(k.item.status) && sub.brand?.status !== "approved";
}
